import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import styles from "../../../styles/components/StripeCheckoutModal.module.css";

function useCurrentTheme() {
  const [isDark, setIsDark] = useState(
    document.documentElement.dataset.theme === "dark"
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.dataset.theme === "dark");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

function ModalShell({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>{title || "Pago"}</div>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
}

function StripePaymentForm({ onClose, onDone, returnUrl }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const handleConfirm = async () => {
    if (!stripe || !elements) return;

    setSaving(true);
    setErr("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: returnUrl || window.location.href,
      },
    });

    setSaving(false);

    if (error) {
      setErr(error.message || "No se pudo confirmar el pago.");
      return;
    }

    onDone?.({ paymentIntentId: paymentIntent?.id, status: paymentIntent?.status });
    onClose?.();
  };

  return (
    <div className={styles.formBody}>
      <div className={styles.formHint}>
        Completá los datos del medio de pago. El cargo se procesa al confirmar.
      </div>

      <div className={styles.paymentElementWrapper}>
        <PaymentElement />
      </div>

      {err && (
        <div className={styles.formError}>{err}</div>
      )}

      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          type="button"
          className={styles.confirmButton}
          onClick={handleConfirm}
          disabled={saving || !stripe}
        >
          {saving ? "Procesando..." : "Pagar"}
        </button>
      </div>
    </div>
  );
}

export default function StripeCheckoutModal({
  open,
  onClose,
  clientSecret,
  onDone,
  title,
  returnUrl,
}) {
  const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const isDark = useCurrentTheme();

  const stripePromise = useMemo(() => {
    if (!pk) return null;
    return loadStripe(pk);
  }, [pk]);

  const elementsOptions = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      appearance: {
        theme: isDark ? "night" : "stripe",
        variables: {
          colorPrimary: "#359fbb",
          colorBackground: isDark ? "#1a1a1b" : "#ffffff",
          colorText: isDark ? "#ffffff" : "#1a1a1b",
          colorDanger: isDark ? "#f87171" : "#ef4444",
          borderRadius: "8px",
          fontFamily: "DM Sans, sans-serif",
        },
      },
    };
  }, [clientSecret, isDark]);

  const missingConfig = !pk;
  const missingClientSecret = !clientSecret;

  return (
    <ModalShell open={open} onClose={onClose} title={title || "Checkout"}>
      {missingConfig && (
        <div className={styles.configError}>
          Falta configuración: no está seteada <b>REACT_APP_STRIPE_PUBLISHABLE_KEY</b>.
        </div>
      )}

      {!missingConfig && missingClientSecret && (
        <div className={styles.configError}>
          Falta <b>client_secret</b>. Llamá al endpoint de backend que crea la suscripción y devuelve el client_secret del PaymentIntent.
        </div>
      )}

      {!missingConfig && !missingClientSecret && stripePromise && elementsOptions && (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <StripePaymentForm onClose={onClose} onDone={onDone} returnUrl={returnUrl} />
        </Elements>
      )}
    </ModalShell>
  );
}
